import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import {resolve} from "../../../container";
import {intRangeClosed} from "../../../bootstrap/common/arrays";
import {type CleanableStoryTile} from "./energyBitStoryTile";
import {classMap} from "lit/directives/class-map.js";
import {eventOccurredOutside, KEYS} from "../../../common/utils/events";
import {Timeout} from "../../../common/timeout";
import {TouchControlHandler} from "../../../common/utils/touchControlHandler";
import type {Nullable} from "../../../bootstrap/common/types";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";
import {UnLitElement, WithEopSlots} from "../../../common/elements";
import {Dictionary} from "../dictionary";

export const FADE_DURATION_IN_S = 0.5; //has to be in sync with CSS

@customElement("eop-energy-bit-story-viewer")
export class EopEnergyBitStoryViewer extends WithEopSlots(UnLitElement) {

    @property({reflect: true, type: Boolean})
    private playing: boolean = false;
    @property({reflect: true, type: Boolean})
    private open: boolean = false;
    @property({attribute: "story-image-slot-name"})
    private storyImageSlotName: string;
    @state()
    private progress: number = 0;
    @query(".story-container")
    private storyContainer: HTMLElement;
    @query(".bar-container[animated] .bar.active")
    private activeProgressBar: HTMLElement;
    @query(".close-button")
    private closeButton: HTMLButtonElement;

    private imageCount: number;
    private managingParent: CleanableStoryTile;
    private openerElement: Nullable<HTMLElement>;
    private dictionary: Dictionary;

    public constructor(
        private timeout: Timeout = resolve(Timeout)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.dictionary = Dictionary.of(this);
    }

    public render(): TemplateResult {
        return html`
            <div class="story-container">
                ${this.renderStoryImages()}
                <div class="interaction-overlay">
                    ${this.renderProgress()}
                    ${this.renderControlElements()}
                </div>
                <button class="prev-button"
                        aria-label=${this.dictionary.translate("MSG_PREVIOUS_SLIDE")}
                        @click=${this.previous}
                        ${trackingButtonInteract("energy-bit-previous-button")}
                ></button>
                <button class="next-button"
                        aria-label=${this.dictionary.translate("MSG_NEXT_SLIDE")}
                        @click=${this.nextOrClose}
                        ${trackingButtonInteract("energy-bit-next-button")}
                ></button>
            </div>
            <eop-tab-out-listener .callback=${() => this.closeStory()}></eop-tab-out-listener>
        `;
    }

    private renderStoryImages(): TemplateResult {
        const images = this.resolveSlots(this.storyImageSlotName);
        this.imageCount = images.length;
        return html`
            <div class="story-images" role="group" aria-label="Story image ${this.progress + 1} of ${this.imageCount}">
                ${this.renderImages(images)}
            </div>`;
    }

    private renderImages(images: HTMLElement[]): TemplateResult[] {
        return images.map((image, index) => {
            const classes = classMap({
                "active": index === this.progress
            });
            return html`
                <div class="story-image-container ${classes}">
                    ${image}
                </div>`;
        });
    }

    private renderProgress(): TemplateResult {
        return html`
            <div class="progress">
                <div class="bar-container">${this.renderProgressBars()}</div>
                <div class="bar-container" animated @animationend=${this.nextOrClose}>${this.renderProgressBars()}</div>
            </div>`;
    }

    private renderProgressBars(): TemplateResult[] {
        return intRangeClosed(1, this.imageCount).map((_, index) => {
            const classes = classMap({
                "active": this.playing && index === this.progress,
                "past": index < this.progress,
                "upcoming": index > this.progress
            });
            return html`
                <div class="bar ${classes}"></div>`;
        });
    }

    private renderControlElements(): TemplateResult {
        const playPauseLabel = this.playing ? this.dictionary.translate("MSG_PAUSE") : this.dictionary.translate("MSG_PLAY");
        return html`
            <div class="control">
                <button class="resume-pause-button"
                        aria-label=${playPauseLabel}
                        @click=${this.togglePause}
                        ${trackingButtonInteract("energy-bit-resume-pause-button")}
                >
                    ${this.renderPlayPauseIcon()}
                </button>
                <button class="close-button"
                        aria-label=${this.dictionary.translate("MSG_CLOSE_DIALOG")}
                        @click=${this.closeStory}
                        ${trackingButtonInteract("energy-bit-close-button")}
                >
                    <eop-svg-sprite-icon name="close"></eop-svg-sprite-icon>
                </button>
            </div>
        `;
    }

    private renderPlayPauseIcon(): TemplateResult {
        if (this.playing) {
            return html`<eop-svg-sprite-icon name="pause"></eop-svg-sprite-icon>`;
        } else {
            return html`<eop-svg-sprite-icon name="play"></eop-svg-sprite-icon>`;
        }
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        this.addEventListener("click", (event: MouseEvent) => {
            if (eventOccurredOutside(event, this.storyContainer)) {
                this.closeStory();
            }
        });
        this.addEventListener("contextmenu", (event: Event) => {
            event.preventDefault();
        });
        this.addEventListener("keydown", (event: Event) => {
            const keyEvent = event as KeyboardEvent;
            if (keyEvent.key === KEYS.ESCAPE) {
                this.closeStory();
            } else if (keyEvent.key === KEYS.ARROW_LEFT) {
                this.previous();
            } else if (keyEvent.key === KEYS.ARROW_RIGHT) {
                this.nextOrClose();
            }
        });

        new TouchControlHandler([this.storyContainer])
            .onTouchLeft(() => this.previous())
            .onTouchRight(() => this.nextOrClose())
            .onTouchLongPress(() => this.pause())
            .onTouchEnd(() => this.resume())
            .activate();

        this.style.setProperty("--bars-count", String(this.imageCount));
    }

    public setManagingParent(tile: CleanableStoryTile): void {
        this.managingParent = tile;
    }

    public setOpener(opener: HTMLElement): void {
        this.openerElement = opener;
    }

    public openStory(): void {
        this.open = true;
        this.progress = 0;
        this.playing = true;
        void this.updateComplete.then(() => this.closeButton?.focus());
    }

    private closeStory(): void {
        this.open = false;
        void this.timeout.delay(() => {
            this.managingParent.cleanUp();
            this.openerElement?.focus();
        }, FADE_DURATION_IN_S * 1000);
    }

    private nextOrClose(): void {
        if (this.isLastImage()) {
            this.closeStory();
        } else {
            this.next();
        }
    }

    private isLastImage(): boolean {
        return this.progress === this.imageCount - 1;
    }

    private togglePause(): void {
        this.playing = !this.playing;
    }

    private resume(): void {
        this.playing = true;
    }

    private pause(): void {
        this.playing = false;
    }

    private previous(): void {
        this.progress = Math.max(0, this.progress - 1);
        this.playing = true;
        void this.timeout.delay(() => this.restartAnimation(), 0);
    }

    private next(): void {
        this.progress++;
        this.playing = true;
    }

    private restartAnimation(): void {
        //https://medium.com/@khalidoghli7/how-to-trigger-a-reflow-for-flushing-css-changes-e89c96030c04
        this.activeProgressBar.style.animation = "none";
        void this.activeProgressBar.offsetWidth;
        this.activeProgressBar.style.animation = "";
    }
}